import {
  Component,
  DestroyRef,
  effect,
  HostListener,
  Input,
  computed,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { merge } from 'rxjs';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

function trimmedRequired(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const v = (control.value ?? '').toString().trim();
    return v ? null : { required: true };
  };
}

/** Requires a valid URL starting with https:// */
function httpsUrlValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = (control.value ?? '').toString().trim();
    if (!raw) return null;
    if (!/^https:\/\//i.test(raw)) {
      return { httpsUrl: true };
    }
    try {
      new URL(raw);
      return null;
    } catch {
      return { httpsUrl: true };
    }
  };
}

/** Requires a valid http:// or https:// URL (API Base URL, etc.). */
function httpOrHttpsUrlValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = (control.value ?? '').toString().trim();
    if (!raw) return null;
    try {
      const url = new URL(raw);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return { invalidUrl: true };
      }
      if (!url.hostname) {
        return { invalidUrl: true };
      }
      return null;
    } catch {
      return { invalidUrl: true };
    }
  };
}
import {
  AuthApiService,
  CatalogueApiService,
  FileApiService,
} from '@angular-workspace/services';
import { NGSILD_DIRECT_PUSH_PYTHON_SAMPLE } from './ngsild-upload-direct-push-sample';
import type { NgsildUploadedFileItem } from './ngsild-upload.models';
import {
  getPreviewUnsupportedMessage,
  isDirectlyPreviewableFileName,
  isPreviewUnsupportedResponse,
  parseNgsildPreviewData,
  type NgsildPreviewTable,
} from './ngsild-upload-preview.util';
import type { ResourceServerId } from '../../dataset-home/dataset-home.models';
import { NgsildItemService } from '../ngsild-item.service';
import type { NgsildOnboardingPayload } from '../ngsild.models';
import type { ModelOnboardingPayload } from '../../../model/model.models';
import {
  normalizeResourceServerId,
} from '../../resource-server.util';
import {
  uploadMethodFromItemStatus,
  type NgsildUploadMethod,
  type VerifiedUploadRequestType,
} from '../upload-request-type.util';

/** UI / emit type for SFTP vs API (payload uses VERIFIED-/UNVERIFIED- compound values). */
export type NgsildUploadRequestType = NgsildUploadMethod;
export interface NgsildUploadOption {
  value: string;
  /** Full name for accessibility */
  label: string;
  description: string;
  icon: 'upload' | 'sftp' | 'api' | 'push';
}

/** Flat modal tab (upload method or data source URL). */
export interface NgsildModalTab {
  id: string;
  label: string;
  icon: NgsildUploadOption['icon'] | 'data-source';
}

export const NGSILD_DATA_SOURCE_TAB_ID = 'data-source' as const;

/** Upload methods that accept user-entered connection details (modal exclusivity). */
type NgsildExclusiveUploadMethodId =
  | 'sftp-upload'
  | 'api-integration'
  | 'upload-file'
  | typeof NGSILD_DATA_SOURCE_TAB_ID;

/** Upload methods allowed per resource server (Add Dataset → Upload now). */
const UPLOAD_OPTIONS_BY_RESOURCE_SERVER: Record<
  ResourceServerId,
  readonly string[]
> = {
  'ngsi-ld': ['sftp-upload', 'api-integration'],
  'file-server': ['upload-file'],
  gateway: ['sftp-upload', 'api-integration', 'direct-push'],
  ogc: ['sftp-upload', 'api-integration'],
};

const ALL_UPLOAD_OPTIONS: readonly NgsildUploadOption[] = [
  {
    value: 'upload-file',
    label: 'Upload File',
    description:
      'Upload your data files directly through the web interface. Supports various file formats including CSV, JSON, and Excel.',
    icon: 'upload',
  },
  {
    value: 'sftp-upload',
    label: 'SFTP Upload',
    description:
      'Securely transfer your data files using SFTP protocol. Configure your SFTP credentials and automate file transfers.',
    icon: 'sftp',
  },
  {
    value: 'api-integration',
    label: 'API Integration',
    description:
      'Integrate with external APIs to automatically fetch and sync data. Set up API endpoints and authentication.',
    icon: 'api',
  },
  {
    value: 'direct-push',
    label: 'Direct Push',
    description:
      'Push data directly to the platform using our API. Ideal for real-time data streaming and automated updates.',
    icon: 'push',
  },
] as const;

export interface NgsildPreviewingFile {
  name: string;
  size: number;
  type: string;
  previewData: unknown;
  parsedData: NgsildPreviewTable | null;
  rawText: string | null;
  previewUnavailableReason: string | null;
}

@Component({
  selector: 'lib-ngsild-upload-files',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './upload-files.component.html',
  styleUrl: './upload-files.component.scss',
})
export class UploadFilesComponent {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fileApiService = inject(FileApiService);
  private readonly catalogueApiService = inject(CatalogueApiService);
  private readonly authApiService = inject(AuthApiService);
  private readonly ngsildItemService = inject(NgsildItemService);

  /** Bumped when reactive forms change validity (computed cannot read FormGroup alone). */
  private readonly formValidityRevision = signal(0);

  /** File format from metadata step (legacy step 3); drives accept types. */
  readonly fileFormat = input<string>('');

  /** Catalogue item / databank id (required for file server uploads). */
  readonly itemId = input<string | null>(null);

  /** Metadata from create step — used to build the same PUT `item` payload as modify asset. */
  readonly onboardingPayload = input<
    NgsildOnboardingPayload | ModelOnboardingPayload | null
  >(null);

  /** Dataset (default) or model onboarding — affects data-source update API. */
  readonly assetKind = input<'dataset' | 'model'>('dataset');

  readonly modalAssetKindLabel = computed(() =>
    this.assetKind() === 'model' ? 'Model' : 'Dataset',
  );

  /** Shown at the end of the modal tab row. */
  readonly modalDatasetLabel = input<string>('');

  /** Resource server for this dataset (drives which upload method tabs are shown). */
  private readonly resourceServerType = signal<ResourceServerId>('ngsi-ld');

  @Input()
  set resourceServer(value: ResourceServerId | string | null | undefined) {
    this.resourceServerType.set(normalizeResourceServerId(value ?? 'ngsi-ld'));
  }

  /** `modal` hides the page title (shown by shell) and tightens spacing. */
  readonly layout = input<'default' | 'modal'>('default');

  /**
   * When set in modal layout, restricts the UI to a single upload method
   * (e.g. `upload-file` for Manage Assets “File Details”).
   */
  readonly lockedUploadMethod = input<string | null>(null);

  /**
   * When set in modal layout, restricts top-level tabs to one mode
   * (e.g. `data-source` when the asset already has a `mediaURL`).
   */
  readonly lockedModalMode = input<'upload-files' | 'data-source' | null>(null);

  /** Prefill the Data Source URL field (e.g. existing catalogue `mediaURL`). */
  readonly existingMediaUrl = input<string | null>(null);

  /** When false, hides ← Previous in modal footer (e.g. Manage Assets View Files). */
  readonly showModalPreviousButton = input(true);

  /**
   * When set with modal `layout` and `itemId`, loads the prior submission from
   * `GET …/auth/asset/request?type=SFTP|API|VERIFIED-SFTP|…&assetId=…` and shows it read-only (Manage Assets details).
   */
  readonly existingAssetRequestType = input<
    NgsildUploadRequestType | VerifiedUploadRequestType | null
  >(null);

  /** Dismiss the host modal (e.g. Close). */
  readonly modalDismiss = output<void>();

  /** Go back from upload modal (e.g. to post-create success). */
  readonly modalPrevious = output<void>();

  /** File upload publish APIs succeeded — host shows dataset-created modal. */
  readonly publishSuccess = output<void>();

  /**
   * SFTP / API integration request submitted (old `asset/request` flow).
   * Host shows “Request sent to Platform Admin” modal.
   */
  readonly uploadRequestSuccess = output<{ type: NgsildUploadRequestType }>();

  /**
   * Direct Push submitted in modal layout — host shows success overlay and
   * navigates to Create New on confirm.
   */
  readonly directPushSuccess = output<void>();

  /** User confirmed a repository / data source URL (modal **Data Source URL** flow). */
  readonly modalDataSourceConnect = output<{ url: string }>();

  readonly selectedUploadOption = signal('');
  readonly selectedFiles = signal<File[]>([]);
  readonly uploadedFiles = signal<NgsildUploadedFileItem[]>([]);
  readonly uploading = signal(false);
  readonly isPublishing = signal(false);
  readonly isConnectingDataSource = signal(false);
  readonly isPublished = signal(false);
  readonly isUploadSuccessModalOpen = signal(false);
  readonly previewModalOpen = signal(false);
  readonly previewingFile = signal<NgsildPreviewingFile | null>(null);

  readonly sftpForm: FormGroup = this.fb.group({
    sftpHost: ['', [Validators.required]],
    port: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
    username: ['', [Validators.required]],
    password: ['', [Validators.required]],
    remotePath: ['', [Validators.required]],
    localFilePath: ['', [Validators.required]],
    optionalNotes: [''],
  });

  /** Modal-only: connect external repository by URL. */
  readonly dataSourceUrlForm: FormGroup = this.fb.group({
    repositoryUrl: ['', [trimmedRequired(), httpsUrlValidator()]],
  });

  readonly apiIntegrationForm: FormGroup = this.fb.group({
    dataSourceName: ['', [Validators.required]],
    apiBaseUrl: ['', [trimmedRequired(), httpOrHttpsUrlValidator()]],
    endpointPath: ['', [Validators.required]],
    httpMethod: ['', [Validators.required]],
    customHeaders: [''],
    queryParameters: [''],
    bodyPayload: [''],
    authenticationDetails: [''],
    authenticationType: ['', [Validators.required]],
    responseFormat: ['', [Validators.required]],
    dataPathInResponse: ['', [Validators.required]],
    expectedPullFrequency: ['', [Validators.required]],
    additionalNotes: [''],
  });

  readonly httpMethodOptions = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
  readonly authenticationTypeOptions = [
    'Bearer Token',
    'API Key',
    'Basic Auth',
    'OAuth 2.0',
    'Custom',
  ];
  readonly responseFormatOptions = ['JSON', 'XML', 'CSV', 'Text'];

  readonly isHttpMethodDropdownOpen = signal(false);
  readonly isAuthenticationTypeDropdownOpen = signal(false);
  readonly isResponseFormatDropdownOpen = signal(false);

  /** All methods (template filters via {@link isUploadMethodAllowed}). */
  readonly allUploadMethodOptions: readonly NgsildUploadOption[] =
    ALL_UPLOAD_OPTIONS;

  readonly visibleUploadOptions = computed(() => {
    const locked = this.lockedUploadMethod()?.trim();
    if (locked) {
      const opt = ALL_UPLOAD_OPTIONS.find((o) => o.value === locked);
      return opt ? [opt] : [];
    }
    const server = this.resourceServerType();
    const allowed =
      UPLOAD_OPTIONS_BY_RESOURCE_SERVER[server] ??
      UPLOAD_OPTIONS_BY_RESOURCE_SERVER['ngsi-ld'];
    return ALL_UPLOAD_OPTIONS.filter((o) => allowed.includes(o.value));
  });

  /** Whether a given upload method tab should render for the current resource server. */
  isUploadMethodAllowed(method: string): boolean {
    const locked = this.lockedUploadMethod()?.trim();
    if (locked) {
      return method === locked;
    }
    const server = this.resourceServerType();
    const allowed =
      UPLOAD_OPTIONS_BY_RESOURCE_SERVER[server] ??
      UPLOAD_OPTIONS_BY_RESOURCE_SERVER['ngsi-ld'];
    return allowed.includes(method);
  }

  /**
   * Flat modal tabs for the current resource server, e.g.
   * NGSI-LD: SFTP, API, Data Source URL — FILE: Upload File, Data Source URL — Gateway: SFTP, API, Direct push, Data Source URL.
   */
  readonly modalTabs = computed((): readonly NgsildModalTab[] => {
    if (this.layout() !== 'modal') {
      return [];
    }
    const locked = this.lockedUploadMethod()?.trim();
    const lockedModal = this.lockedModalMode()?.trim();
    if (lockedModal === NGSILD_DATA_SOURCE_TAB_ID) {
      return [
        {
          id: NGSILD_DATA_SOURCE_TAB_ID,
          label: this.getModalTabLabel(NGSILD_DATA_SOURCE_TAB_ID),
          icon: 'data-source',
        },
      ];
    }
    if (locked) {
      const opt = ALL_UPLOAD_OPTIONS.find((o) => o.value === locked);
      if (!opt) {
        return [];
      }
      return [
        {
          id: locked,
          label: this.getModalTabLabel(locked),
          icon: opt.icon,
        },
      ];
    }
    const server = this.resourceServerType();
    const allowed =
      UPLOAD_OPTIONS_BY_RESOURCE_SERVER[server] ??
      UPLOAD_OPTIONS_BY_RESOURCE_SERVER['ngsi-ld'];
    const tabs: NgsildModalTab[] = [];
    for (const method of allowed) {
      const opt = ALL_UPLOAD_OPTIONS.find((o) => o.value === method);
      if (opt) {
        tabs.push({
          id: opt.value,
          label: this.getModalTabLabel(opt.value),
          icon: opt.icon,
        });
      }
    }
    if (this.isDataSourceUrlEnabled()) {
      tabs.push({
        id: NGSILD_DATA_SOURCE_TAB_ID,
        label: this.getModalTabLabel(NGSILD_DATA_SOURCE_TAB_ID),
        icon: 'data-source',
      });
    }
    return tabs;
  });

  readonly isDataSourceTabSelected = computed(
    () => this.selectedUploadOption() === NGSILD_DATA_SOURCE_TAB_ID,
  );

  /**
   * Data Source URL tab visibility.
   * NGSI-LD / Gateway: always offered with SFTP/API (metadata-only Upload Later assets included).
   * File server: public/open datasets only.
   */
  readonly isDataSourceUrlEnabled = computed(() => {
    const server = this.resourceServerType();
    if (server === 'ngsi-ld' || server === 'gateway') {
      return true;
    }
    const permission = this.onboardingPayload()?.basic?.permission;
    if (!permission) {
      return true;
    }
    const p = String(permission).trim().toUpperCase();
    return p === 'OPEN' || p === 'PUBLIC';
  });

  /** Hide inner tabs when only one method (e.g. View Files modal already has Upload Files tab). */
  readonly showModalTabBar = computed(
    () => this.layout() === 'modal' && this.modalTabs().length > 1,
  );

  readonly directPushCode = NGSILD_DIRECT_PUSH_PYTHON_SAMPLE;

  /** Inline banner (replaces app ToasterService inside the feature lib). */
  readonly banner = signal<{ type: 'success' | 'error'; text: string } | null>(
    null,
  );

  /** Shown when the user tries to switch tabs while another method has draft data. */
  readonly tabSwitchConfirmTarget = signal<string | null>(null);

  readonly isDropzoneActive = signal(false);
  private dropZoneDepth = 0;

  /** Loading prior SFTP/API submission for read-only view (Manage Assets). */
  readonly isLoadingExistingAssetRequest = signal(false);

  /** After a row from `/asset/request` was patched into SFTP or API forms (view-only). */
  readonly existingUploadConfigReadOnly = signal(false);

  private existingUploadedConfigSeq = 0;

  constructor() {
    const bumpFormValidity = (): void => {
      this.formValidityRevision.update((n) => n + 1);
    };
    merge(
      this.sftpForm.statusChanges,
      this.sftpForm.valueChanges,
      this.apiIntegrationForm.statusChanges,
      this.apiIntegrationForm.valueChanges,
      this.dataSourceUrlForm.statusChanges,
      this.dataSourceUrlForm.valueChanges,
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        bumpFormValidity();
      });

    effect(() => {
      const server = this.resourceServerType();
      const locked = this.lockedUploadMethod()?.trim();
      const lockedModal = this.lockedModalMode()?.trim();
      const tabIds = this.modalTabs().map((t) => t.id);
      untracked(() => {
        if (lockedModal === NGSILD_DATA_SOURCE_TAB_ID) {
          if (this.selectedUploadOption() !== NGSILD_DATA_SOURCE_TAB_ID) {
            this.selectedUploadOption.set(NGSILD_DATA_SOURCE_TAB_ID);
          }
          return;
        }
        if (locked) {
          if (this.selectedUploadOption() !== locked) {
            this.selectedUploadOption.set(locked);
            this.bumpMethodHintAnimation();
          }
          return;
        }
        const current = this.selectedUploadOption();
        if (!current || !tabIds.includes(current)) {
          this.selectedUploadOption.set(
            tabIds[0] ?? this.defaultUploadOptionForServer(server),
          );
          this.bumpMethodHintAnimation();
        }
      });
    });

    effect(() => {
      const url = (this.existingMediaUrl() ?? '').trim();
      const readOnly = this.isExistingDataSourceReadOnly();
      void url;
      void readOnly;
      untracked(() => {
        if (readOnly) {
          if (url) {
            const ctrl = this.dataSourceUrlForm.get('repositoryUrl');
            if ((ctrl?.value ?? '').toString().trim() !== url) {
              this.dataSourceUrlForm.patchValue({ repositoryUrl: url }, { emitEvent: false });
            }
          }
          if (this.dataSourceUrlForm.enabled) {
            this.dataSourceUrlForm.disable({ emitEvent: false });
          }
          return;
        }
        if (this.dataSourceUrlForm.disabled) {
          this.dataSourceUrlForm.enable({ emitEvent: false });
        }
        if (!url) {
          return;
        }
        const ctrl = this.dataSourceUrlForm.get('repositoryUrl');
        if ((ctrl?.value ?? '').toString().trim() === url) {
          return;
        }
        this.dataSourceUrlForm.patchValue({ repositoryUrl: url }, { emitEvent: false });
        ctrl?.updateValueAndValidity({ emitEvent: false });
      });
    });

    effect(() => {
      const layout = this.layout();
      const assetId = (this.itemId() ?? '').trim();
      const reqType = this.existingAssetRequestType();
      void layout;
      void assetId;
      void reqType;
      untracked(() => void this.syncExistingUploadedConfig(layout, assetId, reqType));
    });
  }

  private defaultUploadOptionForServer(server: ResourceServerId): string {
    const allowed =
      UPLOAD_OPTIONS_BY_RESOURCE_SERVER[server] ??
      UPLOAD_OPTIONS_BY_RESOURCE_SERVER['ngsi-ld'];
    return allowed[0] ?? 'upload-file';
  }

  readonly isUploadFileSelected = computed(
    () => this.selectedUploadOption() === 'upload-file',
  );
  readonly isSftpUploadSelected = computed(
    () => this.selectedUploadOption() === 'sftp-upload',
  );
  readonly isApiIntegrationSelected = computed(
    () => this.selectedUploadOption() === 'api-integration',
  );
  readonly isDirectPushSelected = computed(
    () => this.selectedUploadOption() === 'direct-push',
  );

  /** Manage Assets: asset already has `mediaURL` — view only, footer shows Close. */
  readonly isExistingDataSourceReadOnly = computed(() => {
    if (this.layout() !== 'modal') {
      return false;
    }
    if (this.lockedModalMode()?.trim() !== 'data-source') {
      return false;
    }
    return !!(this.existingMediaUrl() ?? '').trim();
  });

  /**
   * Onboarding “Upload now” modal: only one connection type may hold draft data
   * (SFTP, API, file upload, or data source URL) until cleared or submitted.
   */
  readonly isExclusiveUploadMode = computed(() => {
    if (this.layout() !== 'modal') {
      return false;
    }
    if (this.existingUploadConfigReadOnly() || this.isLoadingExistingAssetRequest()) {
      return false;
    }
    if (this.isExistingDataSourceReadOnly()) {
      return false;
    }
    if (this.lockedUploadMethod()?.trim() || this.lockedModalMode()?.trim()) {
      return false;
    }
    return true;
  });

  readonly draftedUploadMethodId = computed((): NgsildExclusiveUploadMethodId | null => {
    this.formValidityRevision();
    this.selectedFiles();
    this.uploadedFiles();
    if (!this.isExclusiveUploadMode()) {
      return null;
    }
    const drafted = this.detectDraftedUploadMethods();
    if (drafted.length === 0) {
      return null;
    }
    if (drafted.length === 1) {
      return drafted[0]!;
    }
    const selected = this.selectedUploadOption();
    if (drafted.includes(selected as NgsildExclusiveUploadMethodId)) {
      return selected as NgsildExclusiveUploadMethodId;
    }
    return drafted[0]!;
  });

  readonly canModalPublish = computed(() => {
    this.formValidityRevision();
    if (this.layout() !== 'modal') return false;
    if (this.isExistingDataSourceReadOnly()) {
      return true;
    }
    if (this.existingUploadConfigReadOnly() || this.isLoadingExistingAssetRequest()) {
      return false;
    }
    if (this.uploading() || this.isPublishing() || this.isConnectingDataSource()) {
      return false;
    }
    if (this.isExclusiveUploadMode()) {
      const draft = this.draftedUploadMethodId();
      const selected = this.selectedUploadOption();
      if (draft && draft !== selected) {
        return false;
      }
    }
    if (this.isDataSourceTabSelected()) {
      return this.dataSourceUrlForm.valid;
    }
    const opt = this.selectedUploadOption();
    if (opt === 'upload-file') {
      if (this.selectedFiles().length > 0) return false;
      return (
        this.uploadedFiles().length > 0 && this.isAllUploadsCompleted()
      );
    }
    if (opt === 'sftp-upload') return this.sftpForm.valid;
    if (opt === 'api-integration') return this.apiIntegrationForm.valid;
    if (opt === 'direct-push') return true;
    return false;
  });

  readonly modalPrimaryFooterLabel = computed(() =>
    this.isExistingDataSourceReadOnly() ? 'Close' : 'Submit',
  );

  readonly clearSelectedFilesLabel = computed(() =>
    this.selectedFiles().length === 1 ? 'Clear' : 'Clear All',
  );

  readonly uploadSelectedFilesLabel = computed(() => {
    if (this.uploading()) return 'Uploading…';
    return this.selectedFiles().length === 1 ? 'Upload' : 'Upload All';
  });

  /** Currently selected method metadata (hint text, a11y). */
  readonly activeUploadMethod = computed(() => {
    const key = this.selectedUploadOption();
    return this.visibleUploadOptions().find((o) => o.value === key) ?? null;
  });

  /** Bumped when the method changes so the hint replay its enter animation. */
  readonly methodHintAnimationKey = signal(0);

  private bumpMethodHintAnimation(): void {
    this.methodHintAnimationKey.update((k) => k + 1);
  }

  private unwrapAssetRequestRows(response: unknown): unknown[] {
    const payload =
      response && typeof response === 'object'
        ? (response as Record<string, unknown>)['data'] ?? response
        : null;
    const resultArray =
      payload && typeof payload === 'object'
        ? (payload as Record<string, unknown>)['result']
        : null;
    return Array.isArray(resultArray) ? resultArray : [];
  }

  private normalizeAssetRowId(candidate: Record<string, unknown>): string | null {
    const req =
      (candidate['assetRequest'] as Record<string, unknown> | undefined) ??
      candidate;
    const res = candidate['resource'] as Record<string, unknown> | undefined;
    const raw =
      req['assetId'] ??
      req['asset_id'] ??
      candidate['assetId'] ??
      candidate['asset_id'] ??
      req['resourceId'] ??
      req['resource_id'] ??
      candidate['resourceId'] ??
      candidate['resource_id'] ??
      req['itemId'] ??
      req['item_id'] ??
      candidate['itemId'] ??
      candidate['item_id'] ??
      req['id'] ??
      candidate['id'] ??
      res?.['id'];
    if (raw == null) return null;
    const s = String(raw).trim();
    return s ? s : null;
  }

  private findAssetRequestRowForAssetId(
    rows: unknown[],
    assetId: string,
  ): Record<string, unknown> | null {
    const want = assetId.trim().toLowerCase();
    for (const row of rows) {
      if (!row || typeof row !== 'object' || Array.isArray(row)) {
        continue;
      }
      const rec = row as Record<string, unknown>;
      const got = this.normalizeAssetRowId(rec);
      if (got && got.toLowerCase() === want) {
        return rec;
      }
    }
    return null;
  }

  private extractSftpFormPatch(src: Record<string, unknown>): Record<string, string> {
    const req =
      (src['assetRequest'] as Record<string, unknown> | undefined) ?? src;
    const infoRaw =
      req['additionalInfo'] ??
      req['additional_info'] ??
      src['additionalInfo'] ??
      src['additional_info'] ??
      src['info'] ??
      {};
    const info =
      infoRaw && typeof infoRaw === 'object' && !Array.isArray(infoRaw)
        ? (infoRaw as Record<string, unknown>)
        : {};
    const sftpNested = info['sftp'] as Record<string, unknown> | undefined;

    const str = (v: unknown): string =>
      v == null || typeof v === 'object' ? '' : String(v);

    return {
      sftpHost: str(
        info['sftpHost'] ??
          src['sftpHost'] ??
          src['host'] ??
          src['sftp_host'] ??
          sftpNested?.['host'],
      ),
      port: str(
        info['sftpPort'] ??
          src['port'] ??
          src['sftpPort'] ??
          src['sftp_port'] ??
          sftpNested?.['port'],
      ),
      username: str(
        info['sftpUser'] ??
          src['username'] ??
          src['userName'] ??
          src['user_name'] ??
          sftpNested?.['username'],
      ),
      password: str(
        info['sftpPass'] ??
          src['password'] ??
          src['pass'] ??
          sftpNested?.['password'],
      ),
      remotePath: str(
        info['remotePath'] ??
          src['remotePath'] ??
          src['remote_path'] ??
          sftpNested?.['remotePath'],
      ),
      localFilePath: str(
        info['localPath'] ??
          info['localFilePath'] ??
          src['localFilePath'] ??
          src['local_file_path'] ??
          sftpNested?.['localFilePath'],
      ),
      optionalNotes: str(
        info['description'] ??
          src['optionalNotes'] ??
          src['notes'] ??
          src['description'],
      ),
    };
  }

  private extractApiFormPatch(src: Record<string, unknown>): Record<string, string> {
    const req =
      (src['assetRequest'] as Record<string, unknown> | undefined) ?? src;
    const infoRaw =
      req['additionalInfo'] ??
      req['additional_info'] ??
      src['additionalInfo'] ??
      src['additional_info'] ??
      src['info'] ??
      {};
    const info =
      infoRaw && typeof infoRaw === 'object' && !Array.isArray(infoRaw)
        ? (infoRaw as Record<string, unknown>)
        : {};

    const str = (v: unknown): string =>
      v == null || typeof v === 'object' ? '' : String(v);

    return {
      dataSourceName: str(
        info['dataSourceName'] ??
          info['sourceName'] ??
          req['sourceName'] ??
          src['dataSourceName'],
      ),
      apiBaseUrl: str(
        info['apiBaseUrl'] ??
          info['baseUrl'] ??
          req['apiBaseUrl'] ??
          src['apiBaseUrl'],
      ),
      endpointPath: str(
        info['endpointPath'] ??
          info['endpoint'] ??
          req['endpointPath'] ??
          src['endpointPath'],
      ),
      httpMethod: str(
        info['httpMethod'] ??
          info['method'] ??
          req['httpMethod'] ??
          src['httpMethod'],
      ),
      customHeaders: str(
        info['customHeaders'] ??
          info['headers'] ??
          req['headers'] ??
          src['customHeaders'],
      ),
      queryParameters: str(
        info['queryParameters'] ??
          info['queryParams'] ??
          req['queryParams'] ??
          src['queryParameters'],
      ),
      bodyPayload: str(
        info['bodyPayload'] ??
          info['body'] ??
          req['bodyPayload'] ??
          src['bodyPayload'],
      ),
      authenticationDetails: str(
        info['authenticationDetails'] ??
          info['authDetails'] ??
          req['authDetails'] ??
          src['authenticationDetails'],
      ),
      authenticationType: str(
        info['authenticationType'] ??
          info['authType'] ??
          req['authType'] ??
          src['authenticationType'],
      ),
      responseFormat: str(
        info['responseFormat'] ?? req['responseFormat'] ?? src['responseFormat'],
      ),
      dataPathInResponse: str(
        info['dataPathInResponse'] ??
          info['dataPath'] ??
          req['dataPath'] ??
          src['dataPathInResponse'],
      ),
      expectedPullFrequency: str(
        info['expectedPullFrequency'] ??
          info['pullFrequency'] ??
          info['schedule'] ??
          req['schedule'] ??
          src['expectedPullFrequency'],
      ),
      additionalNotes: str(
        info['additionalNotes'] ??
          info['description'] ??
          info['notes'] ??
          src['additionalNotes'],
      ),
    };
  }

  private releaseExistingUploadConfigForms(): void {
    if (!this.existingUploadConfigReadOnly()) {
      return;
    }
    this.sftpForm.enable({ emitEvent: false });
    this.apiIntegrationForm.enable({ emitEvent: false });
    this.existingUploadConfigReadOnly.set(false);
    this.formValidityRevision.update((n) => n + 1);
  }

  private async syncExistingUploadedConfig(
    layout: 'default' | 'modal',
    assetId: string,
    reqType: NgsildUploadRequestType | VerifiedUploadRequestType | null,
  ): Promise<void> {
    const shouldFetch =
      layout === 'modal' && assetId !== '' && reqType != null;
    if (!shouldFetch) {
      this.existingUploadedConfigSeq++;
      this.isLoadingExistingAssetRequest.set(false);
      this.releaseExistingUploadConfigForms();
      return;
    }

    const seq = ++this.existingUploadedConfigSeq;
    this.isLoadingExistingAssetRequest.set(true);
    this.releaseExistingUploadConfigForms();

    try {
      const response = await this.authApiService.getAssetRequests(
        1,
        10,
        reqType,
        undefined,
        assetId,
      );
      if (seq !== this.existingUploadedConfigSeq) {
        return;
      }
      const rows = this.unwrapAssetRequestRows(response);
      const match = this.findAssetRequestRowForAssetId(rows, assetId);
      if (!match) {
        // No prior submission — keep forms editable so Manage Assets can add connection details.
        return;
      }

      if (reqType === 'SFTP' || uploadMethodFromItemStatus(reqType) === 'SFTP') {
        const patch = this.extractSftpFormPatch(match);
        this.sftpForm.enable({ emitEvent: false });
        this.sftpForm.reset(patch, { emitEvent: false });
        this.sftpForm.disable({ emitEvent: false });
        this.selectedUploadOption.set('sftp-upload');
      } else {
        const patch = this.extractApiFormPatch(match);
        this.apiIntegrationForm.enable({ emitEvent: false });
        this.apiIntegrationForm.reset(patch, { emitEvent: false });
        this.apiIntegrationForm.disable({ emitEvent: false });
        this.selectedUploadOption.set('api-integration');
      }
      this.existingUploadConfigReadOnly.set(true);
      this.formValidityRevision.update((n) => n + 1);
      this.bumpMethodHintAnimation();
    } catch (error: unknown) {
      if (seq !== this.existingUploadedConfigSeq) {
        return;
      }
      const msg =
        (error as { message?: string })?.message ??
        'Failed to load saved configuration.';
      this.showBanner('error', msg);
      console.error('Existing asset upload config load failed:', error);
    } finally {
      if (seq === this.existingUploadedConfigSeq) {
        this.isLoadingExistingAssetRequest.set(false);
      }
    }
  }

  private showBanner(type: 'success' | 'error', text: string): void {
    this.banner.set({ type, text });
    window.setTimeout(() => this.banner.set(null), 4500);
  }

  /** Always permitted alongside the chosen metadata format (onboarding flow). */
  private readonly defaultUploadExtensions = ['.csv', '.txt'];

  private formatToExtensions(format: string): string[] {
    const normalized = (format || '').trim().toLowerCase();
    if (!normalized) return [...this.defaultUploadExtensions];
    const map: { [key: string]: string[] } = {
      csv: ['.csv'],
      tsv: ['.tsv'],
      json: ['.json'],
      xml: ['.xml'],
      xlsx: ['.xlsx'],
      parquet: ['.parquet'],
      avro: ['.avro'],
      pdf: ['.pdf'],
      docx: ['.docx'],
      txt: ['.txt'],
      png: ['.png'],
      jpg: ['.jpg', '.jpeg'],
      jpeg: ['.jpg', '.jpeg'],
      mp4: ['.mp4'],
      mp3: ['.mp3'],
      yaml: ['.yaml', '.yml'],
      yml: ['.yaml', '.yml'],
      jsonl: ['.jsonl'],
    };
    const chosen = map[normalized] ? [...map[normalized]] : ['.' + normalized];
    return Array.from(new Set([...chosen, ...this.defaultUploadExtensions]));
  }

  getAllowedExtensions(): string[] {
    return this.formatToExtensions(this.fileFormat());
  }

  getAcceptAttribute(): string {
    return this.getAllowedExtensions().join(',');
  }

  getAllowedFileTypesLabel(): string {
    const labels = this.getAllowedExtensions().map((e) =>
      e.replace(/^\./, '').toUpperCase(),
    );
    return Array.from(new Set(labels))
      .sort((a, b) => a.localeCompare(b))
      .join(', ');
  }

  /** Allowed format labels for modal drop-zone (metadata file format + defaults). */
  getModalPrimaryFormatLabel(): string {
    return this.getAllowedFileTypesLabel();
  }

  private getFileExtension(name: string): string {
    const i = name.lastIndexOf('.');
    return i >= 0 ? '.' + name.slice(i + 1).toLowerCase() : '';
  }

  private isAllowedFile(file: File): boolean {
    return this.getAllowedExtensions().some(
      (e) => e.toLowerCase() === this.getFileExtension(file.name),
    );
  }

  isUploadMethodTabBlocked(tabId: string): boolean {
    if (!this.isExclusiveUploadMode()) {
      return false;
    }
    const draft = this.draftedUploadMethodId();
    if (!draft) {
      return false;
    }
    return tabId !== draft;
  }

  clearAllUploadMethodDrafts(): void {
    this.sftpForm.reset();
    this.apiIntegrationForm.reset();
    this.dataSourceUrlForm.reset();
    this.clearAllFiles();
    this.isHttpMethodDropdownOpen.set(false);
    this.isAuthenticationTypeDropdownOpen.set(false);
    this.isResponseFormatDropdownOpen.set(false);
    this.formValidityRevision.update((n) => n + 1);
  }

  /** Select a flat modal tab (upload method or data source URL). */
  selectModalTab(tabId: string): void {
    const tabs = this.modalTabs();
    if (!tabs.some((t) => t.id === tabId)) {
      return;
    }
    if (tabId === this.selectedUploadOption()) {
      return;
    }
    if (this.isUploadMethodTabBlocked(tabId)) {
      this.tabSwitchConfirmTarget.set(tabId);
      return;
    }
    this.performModalTabSwitch(tabId);
  }

  confirmTabSwitch(): void {
    const tabId = this.tabSwitchConfirmTarget();
    if (!tabId) {
      return;
    }
    this.tabSwitchConfirmTarget.set(null);
    this.clearAllUploadMethodDrafts();
    this.performModalTabSwitch(tabId);
  }

  cancelTabSwitch(): void {
    this.tabSwitchConfirmTarget.set(null);
  }

  private performModalTabSwitch(tabId: string): void {
    if (tabId === NGSILD_DATA_SOURCE_TAB_ID) {
      const lockedModal = this.lockedModalMode()?.trim();
      if (lockedModal && lockedModal !== NGSILD_DATA_SOURCE_TAB_ID) {
        return;
      }
      const prev = this.selectedUploadOption();
      this.selectedUploadOption.set(NGSILD_DATA_SOURCE_TAB_ID);
      if (prev !== NGSILD_DATA_SOURCE_TAB_ID) {
        this.dataSourceUrlForm.markAsUntouched();
      }
      return;
    }
    this.selectUploadOption(tabId);
  }

  selectUploadOption(option: string): void {
    const allowed = this.visibleUploadOptions().map((o) => o.value);
    if (!allowed.includes(option)) return;
    const prev = this.selectedUploadOption();
    this.selectedUploadOption.set(option);
    if (prev !== option) {
      this.bumpMethodHintAnimation();
    }
    if (option !== 'upload-file') {
      this.selectedFiles.set([]);
    }
    this.formValidityRevision.update((n) => n + 1);
  }

  getModalTabLabel(tabId: string): string {
    switch (tabId) {
      case 'sftp-upload':
        return 'SFTP';
      case 'api-integration':
        return 'API';
      case 'direct-push':
        return 'Direct push';
      case 'upload-file':
        return 'Upload File';
      case NGSILD_DATA_SOURCE_TAB_ID:
        return 'Data Source URL';
      default:
        return (
          ALL_UPLOAD_OPTIONS.find((o) => o.value === tabId)?.label ?? tabId
        );
    }
  }

  async onSubmitDataSourceUrlForm(): Promise<void> {
    if (!this.canSubmitExclusiveUploadMethod(NGSILD_DATA_SOURCE_TAB_ID)) {
      return;
    }
    if (!this.isDataSourceUrlEnabled()) {
      return;
    }
    const ctrl = this.dataSourceUrlForm.get('repositoryUrl');
    const trimmed = (ctrl?.value ?? '').toString().trim();
    this.dataSourceUrlForm.patchValue({ repositoryUrl: trimmed }, { emitEvent: false });
    ctrl?.updateValueAndValidity({ emitEvent: true });
    if (!this.dataSourceUrlForm.valid) {
      this.dataSourceUrlForm.markAllAsTouched();
      return;
    }

    const itemId = (this.itemId() ?? '').trim();
    const onboarding = this.onboardingPayload();
    if (!itemId || !onboarding) {
      this.showBanner(
        'error',
        this.assetKind() === 'model'
          ? 'Model is not ready. Close this dialog and open upload again from the success step.'
          : 'Dataset is not ready. Close this dialog and open upload again from the success step.',
      );
      return;
    }

    try {
      this.isConnectingDataSource.set(true);
      if (this.assetKind() === 'model') {
        await this.ngsildItemService.updateModelWithDataSourceUrl(
          itemId,
          trimmed,
          onboarding as ModelOnboardingPayload,
        );
      } else {
        await this.ngsildItemService.updateDatasetWithDataSourceUrl(
          itemId,
          trimmed,
          onboarding as NgsildOnboardingPayload,
          this.resourceServerType(),
        );
      }
      this.modalDataSourceConnect.emit({ url: trimmed });
      this.showBanner('success', 'Data source connected successfully.');
      this.publishSuccess.emit();
    } catch (error: unknown) {
      const msg =
        (error as { message?: string })?.message ??
        'Failed to connect data source. Please try again.';
      this.showBanner('error', msg);
      console.error('Error updating dataset with data source URL:', error);
    } finally {
      this.isConnectingDataSource.set(false);
    }
  }

  onModalPrevious(): void {
    this.modalPrevious.emit();
  }

  onModalFooterPrimary(): void {
    if (!this.canModalPublish()) {
      return;
    }
    if (this.isExistingDataSourceReadOnly()) {
      this.modalDismiss.emit();
      return;
    }
    void this.onModalFooterPublish();
  }

  async onModalFooterPublish(): Promise<void> {
    if (!this.canModalPublish()) return;
    if (!this.canSubmitExclusiveUploadMethod(this.selectedUploadOption())) {
      return;
    }
    if (this.layout() === 'modal' && this.isDataSourceTabSelected()) {
      await this.onSubmitDataSourceUrlForm();
      return;
    }
    const opt = this.selectedUploadOption();
    if (opt === 'upload-file') {
      await this.publishDatabank();
      return;
    }
    if (opt === 'sftp-upload') {
      await this.onSubmitSftpForm();
      return;
    }
    if (opt === 'api-integration') {
      await this.onSubmitApiIntegrationForm();
      return;
    }
    if (opt === 'direct-push') {
      await this.onSubmitDirectPush();
    }
  }

  isAllUploadsCompleted(): boolean {
    const files = this.uploadedFiles();
    return (
      files.length > 0 && files.every((f) => f.status === 'completed')
    );
  }

  hasFilesToShow(): boolean {
    return (
      this.selectedFiles().length > 0 || this.uploadedFiles().length > 0
    );
  }

  selectedFilesTitle(): string {
    const selected = this.selectedFiles().length;
    const uploaded = this.uploadedFiles().length;
    if (uploaded === 0) return `Selected Files (${selected})`;
    if (selected === 0) return `Uploaded Files (${uploaded})`;
    return 'Files';
  }

  private resolveDatabankId(): string {
    const id = (this.itemId() ?? '').trim();
    if (!id) {
      throw new Error(
        'Dataset ID not found. Please complete dataset creation first.',
      );
    }
    return id;
  }

  /** Item id passed from create flow or Manage Assets (no extra catalogue GET needed). */
  private resolveAssetIdForUploadRequest(): string {
    return this.resolveDatabankId();
  }

  private patchUploadedFile(
    file: File,
    patch: Partial<NgsildUploadedFileItem>,
  ): void {
    this.uploadedFiles.update((list) =>
      list.map((f) =>
        f.file.name === file.name && f.file.size === file.size
          ? { ...f, ...patch }
          : f,
      ),
    );
  }

  private findUploadedItem(file: File): NgsildUploadedFileItem | undefined {
    return this.uploadedFiles().find(
      (f) => f.file.name === file.name && f.file.size === file.size,
    );
  }

  async uploadSingleFileFromSelected(index: number): Promise<void> {
    if (this.uploading() || index < 0 || index >= this.selectedFiles().length) {
      return;
    }
    const files = [...this.selectedFiles()];
    const file = files[index];
    const fileItem: NgsildUploadedFileItem = {
      file,
      progress: 0,
      status: 'pending',
    };
    this.uploadedFiles.update((list) => {
      if (list.some((f) => f.file.name === file.name && f.file.size === file.size)) {
        return list;
      }
      return [...list, fileItem];
    });
    files.splice(index, 1);
    this.selectedFiles.set(files);

    this.uploading.set(true);
    try {
      await this.uploadSingleFile(file);
      const latest = this.findUploadedItem(file);
      if (latest?.status === 'completed') {
        this.showBanner('success', 'File uploaded successfully!');
      } else if (latest?.error) {
        this.showBanner('error', latest.error);
      }
    } finally {
      this.uploading.set(false);
    }
  }

  async uploadAllFiles(): Promise<void> {
    if (this.uploading() || this.selectedFiles().length === 0) return;

    const toUpload = [...this.selectedFiles()];
    const newItems: NgsildUploadedFileItem[] = [];
    for (const file of toUpload) {
      const exists = this.uploadedFiles().some(
        (f) => f.file.name === file.name && f.file.size === file.size,
      );
      if (!exists) {
        newItems.push({ file, progress: 0, status: 'pending' });
      }
    }
    if (newItems.length > 0) {
      this.uploadedFiles.update((list) => [...list, ...newItems]);
    }
    this.selectedFiles.set([]);

    this.uploading.set(true);
    try {
      const pending = this.uploadedFiles().filter(
        (f) => f.status === 'pending' || f.status === 'error',
      );
      for (const fileItem of pending) {
        await this.uploadSingleFile(fileItem.file);
      }
      if (this.isAllUploadsCompleted()) {
        this.showBanner('success', 'All files uploaded successfully!');
      }
    } finally {
      this.uploading.set(false);
    }
  }

  private async uploadSingleFile(file: File): Promise<void> {
    try {
      this.patchUploadedFile(file, {
        status: 'uploading',
        progress: 0,
        error: undefined,
      });

      const databankId = this.resolveDatabankId();

      const initiateResponse = await this.fileApiService.initiateUpload(
        databankId,
        file.name,
        file.type || 'application/octet-stream',
        1,
      );

      const responseData =
        (initiateResponse.data as { data?: Record<string, unknown> })?.data ??
        initiateResponse.data;
      const uploadId = (responseData as { uploadId?: string })?.uploadId;
      const parts = (responseData as { parts?: Array<{ presignedUrl: string }> })
        ?.parts;
      const presignedUrls = parts?.map((p) => p.presignedUrl) ?? [];

      if (!uploadId || presignedUrls.length === 0) {
        throw new Error('No presigned URLs received from server');
      }

      this.patchUploadedFile(file, { progress: 10 });

      const uploadResponse = await this.fileApiService.uploadPart(
        presignedUrls[0],
        file,
      );
      const etag =
        uploadResponse.headers.get('ETag')?.replace(/"/g, '') ?? '';
      if (!etag) {
        throw new Error('No ETag received from storage upload');
      }

      this.patchUploadedFile(file, { progress: 40 });

      await this.fileApiService.completeUpload(databankId, uploadId, file.name, [
        { partNumber: 1, eTag: etag },
      ]);
      this.patchUploadedFile(file, { progress: 60 });

      await this.fileApiService.listFiles(databankId, 1000, true);
      this.patchUploadedFile(file, { progress: 80 });

      const previewResponse = await this.fileApiService.generatePreview(
        databankId,
        file.name,
      );

      this.patchUploadedFile(file, {
        progress: 100,
        status: 'completed',
        previewData: previewResponse.data ?? previewResponse,
        uploadId,
        key: file.name,
      });
    } catch (error: unknown) {
      const message = this.getUserFriendlyErrorMessage(error);
      this.patchUploadedFile(file, { status: 'error', error: message });
      console.error('Upload error:', error);
    }
  }

  private getUserFriendlyErrorMessage(error: unknown): string {
    const errorMessage =
      (error as { message?: string })?.message ??
      (error != null ? String(error) : '');

    if (
      errorMessage.includes('0 Unknown Error') ||
      errorMessage.includes('ERR_NETWORK')
    ) {
      return 'Upload failed: Please check your internet connection and try again.';
    }
    if (errorMessage.includes('404')) {
      return 'Upload failed: Service not found. Please contact support.';
    }
    if (
      errorMessage.includes('500') ||
      errorMessage.includes('Internal Server Error')
    ) {
      return 'Upload failed: Server error. Please try again later.';
    }
    if (
      errorMessage.includes('413') ||
      errorMessage.includes('Payload Too Large')
    ) {
      return 'Upload failed: File is too large. Please choose a smaller file.';
    }
    if (errorMessage.includes('403') || errorMessage.includes('Forbidden')) {
      return 'Upload failed: You do not have permission to upload this file.';
    }
    if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
      return 'Upload failed: Please log in again and try.';
    }
    if (errorMessage.includes('timeout') || errorMessage.includes('TimeoutError')) {
      return 'Upload failed: Request timed out. Please try again.';
    }
    if (errorMessage.includes('Dataset ID not found')) {
      return errorMessage;
    }

    return 'Upload failed: Something went wrong. Please try again.';
  }

  async deleteUploadedFile(fileItem: NgsildUploadedFileItem): Promise<void> {
    try {
      const databankId = this.resolveDatabankId();

      if (fileItem.status === 'completed' && fileItem.key) {
        await this.fileApiService.deleteFile(databankId, fileItem.key);
      } else if (fileItem.uploadId && fileItem.key) {
        await this.fileApiService.cancelUpload(
          databankId,
          fileItem.uploadId,
          fileItem.key,
        );
      }

      this.uploadedFiles.update((list) => list.filter((f) => f !== fileItem));
      this.showBanner('success', 'File deleted successfully');
    } catch (error: unknown) {
      const msg =
        (error as { message?: string })?.message ?? 'Unknown error';
      this.showBanner('error', `Failed to delete file: ${msg}`);
      console.error('Error deleting file:', error);
    }
  }

  uploadMoreFiles(input: HTMLInputElement): void {
    input.click();
  }

  async publishDatabank(): Promise<void> {
    if (!this.canSubmitExclusiveUploadMethod('upload-file')) {
      return;
    }
    if (!this.isAllUploadsCompleted()) {
      this.showBanner(
        'error',
        'Upload all selected files before publishing.',
      );
      return;
    }
    try {
      this.isPublishing.set(true);
      const databankId = this.resolveDatabankId();
      const response = await this.fileApiService.processDatabank(
        databankId,
        'all',
      );

      if (response.status === 200 || response.status === 202) {
        try {
          await this.catalogueApiService.postPublishAssets(databankId, {
            dataUploadStatus: true,
          });
        } catch (patchError) {
          console.error('Failed to set dataUploadStatus:', patchError);
        }
        this.isPublished.set(true);
        this.publishSuccess.emit();
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error: unknown) {
      const msg =
        (error as { message?: string })?.message ?? 'Unknown error';
      this.showBanner('error', `Failed to publish: ${msg}`);
      console.error('Error publishing databank:', error);
    } finally {
      this.isPublishing.set(false);
    }
  }

  openFilePicker(input: HTMLInputElement): void {
    input.click();
  }

  dismissBanner(): void {
    this.banner.set(null);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    this.ingestFiles(Array.from(input.files));
    input.value = '';
  }

  onDropZoneDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  onDropZoneDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dropZoneDepth++;
    this.isDropzoneActive.set(true);
  }

  onDropZoneDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dropZoneDepth = Math.max(0, this.dropZoneDepth - 1);
    if (this.dropZoneDepth === 0) {
      this.isDropzoneActive.set(false);
    }
  }

  onDropZoneDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dropZoneDepth = 0;
    this.isDropzoneActive.set(false);
    const list = event.dataTransfer?.files;
    if (!list?.length) return;
    this.ingestFiles(Array.from(list));
  }

  private ingestFiles(newFiles: File[]): void {
    let rejectedCount = 0;
    const current = [...this.selectedFiles()];
    for (const file of newFiles) {
      if (!this.isAllowedFile(file)) {
        rejectedCount++;
        continue;
      }
      if (!current.some((f) => f.name === file.name && f.size === file.size)) {
        current.push(file);
      }
    }
    this.selectedFiles.set(current);
    if (rejectedCount > 0) {
      this.showBanner(
        'error',
        `Only ${this.getAllowedFileTypesLabel()} files can be uploaded. ${rejectedCount} file(s) were not added.`,
      );
    }
  }

  removeFile(index: number): void {
    const next = [...this.selectedFiles()];
    next.splice(index, 1);
    this.selectedFiles.set(next);
  }

  getFileSize(size: number): string {
    if (size < 1024) {
      return size + ' B';
    } else if (size < 1024 * 1024) {
      return (size / 1024).toFixed(2) + ' KB';
    }
    return (size / (1024 * 1024)).toFixed(2) + ' MB';
  }

  copyCodeToClipboard(): void {
    navigator.clipboard.writeText(this.directPushCode).then(
      () => {
        this.showBanner('success', 'Code copied to clipboard!');
      },
      (err) => {
        console.error('Failed to copy code:', err);
        this.showBanner('error', 'Failed to copy code to clipboard');
      },
    );
  }

  downloadCode(): void {
    const blob = new Blob([this.directPushCode], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'databank_uploader.py';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    this.showBanner('success', 'Code downloaded successfully!');
  }

  async onSubmitDirectPush(): Promise<void> {
    let assetId: string;
    try {
      assetId = this.resolveAssetIdForUploadRequest();
    } catch (error: unknown) {
      const msg =
        (error as { message?: string })?.message ??
        'Dataset ID not found. Please complete dataset creation first.';
      this.showBanner('error', msg);
      return;
    }

    try {
      this.isPublishing.set(true);
      await this.ngsildItemService.updateDatasetItemStatus(
        assetId,
        'Direct',
        this.datasetOnboardingPayload(),
        this.resourceServerType(),
      );
      if (this.layout() === 'modal') {
        this.directPushSuccess.emit();
        return;
      }
      this.isUploadSuccessModalOpen.set(true);
    } catch (error: unknown) {
      const msg =
        (error as { message?: string })?.message ??
        'Failed to save Direct Push configuration.';
      this.showBanner('error', msg);
      console.error('Direct Push itemStatus update failed:', error);
    } finally {
      this.isPublishing.set(false);
    }
  }

  async onSubmitSftpForm(): Promise<void> {
    if (this.existingUploadConfigReadOnly()) {
      return;
    }
    if (!this.canSubmitExclusiveUploadMethod('sftp-upload')) {
      return;
    }
    if (!this.sftpForm.valid) {
      this.sftpForm.markAllAsTouched();
      return;
    }
    const assetId = this.resolveAssetIdForUploadRequest();
    try {
      this.isPublishing.set(true);
      const requestType = await this.ngsildItemService.resolveUploadRequestType(
        assetId,
        'SFTP',
      );
      await this.authApiService.createDataUploadRequest({
        type: requestType,
        additional_info: {
          sftpHost: this.sftpForm.value.sftpHost || '',
          sftpPort: this.sftpForm.value.port || '',
          sftpUser: this.sftpForm.value.username || '',
          sftpPass: this.sftpForm.value.password || '',
          remotePath: this.sftpForm.value.remotePath || '',
          localPath: this.sftpForm.value.localFilePath || '',
          description: this.sftpForm.value.optionalNotes || '',
        },
        asset_id: assetId,
      });
      await this.ngsildItemService.updateDatasetItemStatus(
        assetId,
        'SFTP',
        this.datasetOnboardingPayload(),
        this.resourceServerType(),
      );
      if (this.layout() === 'modal') {
        this.uploadRequestSuccess.emit({ type: 'SFTP' });
      } else {
        this.showBanner('success', 'SFTP configuration saved successfully!');
      }
    } catch (error: unknown) {
      const msg =
        (error as { message?: string })?.message ??
        'Failed to submit SFTP request.';
      this.showBanner('error', msg);
      console.error('SFTP upload request failed:', error);
    } finally {
      this.isPublishing.set(false);
    }
  }

  async onSubmitApiIntegrationForm(): Promise<void> {
    if (this.existingUploadConfigReadOnly()) {
      return;
    }
    if (!this.canSubmitExclusiveUploadMethod('api-integration')) {
      return;
    }
    if (!this.apiIntegrationForm.valid) {
      this.apiIntegrationForm.markAllAsTouched();
      return;
    }
    const assetId = this.resolveAssetIdForUploadRequest();
    try {
      this.isPublishing.set(true);
      const requestType = await this.ngsildItemService.resolveUploadRequestType(
        assetId,
        'API',
      );
      await this.authApiService.createDataUploadRequest({
        type: requestType,
        additional_info: {
          sourceName: this.apiIntegrationForm.value.dataSourceName || '',
          apiBaseUrl: this.apiIntegrationForm.value.apiBaseUrl || '',
          endpointPath: this.apiIntegrationForm.value.endpointPath || '',
          httpMethod: this.apiIntegrationForm.value.httpMethod || '',
          headers: this.apiIntegrationForm.value.customHeaders || '',
          queryParams: this.apiIntegrationForm.value.queryParameters || '',
          bodyPayload: this.apiIntegrationForm.value.bodyPayload || '',
          authDetails: this.apiIntegrationForm.value.authenticationDetails || '',
          authType: this.apiIntegrationForm.value.authenticationType || '',
          responseFormat: this.apiIntegrationForm.value.responseFormat || '',
          dataPath: this.apiIntegrationForm.value.dataPathInResponse || '',
          schedule: this.apiIntegrationForm.value.expectedPullFrequency || '',
          notes: this.apiIntegrationForm.value.additionalNotes || '',
        },
        asset_id: assetId,
      });
      await this.ngsildItemService.updateDatasetItemStatus(
        assetId,
        'API',
        this.datasetOnboardingPayload(),
        this.resourceServerType(),
      );
      if (this.layout() === 'modal') {
        this.uploadRequestSuccess.emit({ type: 'API' });
      } else {
        this.showBanner('success', 'API integration configuration saved successfully!');
      }
    } catch (error: unknown) {
      const msg =
        (error as { message?: string })?.message ??
        'Failed to submit API integration request.';
      this.showBanner('error', msg);
      console.error('API integration upload request failed:', error);
    } finally {
      this.isPublishing.set(false);
    }
  }

  clearAllFiles(): void {
    this.selectedFiles.set([]);
    this.uploadedFiles.set([]);
  }

  private detectDraftedUploadMethods(): NgsildExclusiveUploadMethodId[] {
    const drafted: NgsildExclusiveUploadMethodId[] = [];
    if (this.isSftpFormDrafted()) {
      drafted.push('sftp-upload');
    }
    if (this.isApiFormDrafted()) {
      drafted.push('api-integration');
    }
    if (this.isUploadFileDrafted()) {
      drafted.push('upload-file');
    }
    if (this.isDataSourceUrlDrafted()) {
      drafted.push(NGSILD_DATA_SOURCE_TAB_ID);
    }
    return drafted;
  }

  private isSftpFormDrafted(): boolean {
    return this.isFormGroupDrafted(this.sftpForm);
  }

  private isApiFormDrafted(): boolean {
    return this.isFormGroupDrafted(this.apiIntegrationForm);
  }

  private isDataSourceUrlDrafted(): boolean {
    if (!this.isDataSourceUrlEnabled()) {
      return false;
    }
    return this.isFormGroupDrafted(this.dataSourceUrlForm);
  }

  private isUploadFileDrafted(): boolean {
    return this.selectedFiles().length > 0 || this.uploadedFiles().length > 0;
  }

  private isFormGroupDrafted(form: FormGroup): boolean {
    const raw = form.getRawValue() as Record<string, unknown>;
    return Object.values(raw).some(
      (value) => (value ?? '').toString().trim() !== '',
    );
  }

  private canSubmitExclusiveUploadMethod(methodId: string): boolean {
    if (!this.isExclusiveUploadMode()) {
      return true;
    }
    const draft = this.draftedUploadMethodId();
    if (!draft || draft === methodId) {
      return true;
    }
    this.showBanner(
      'error',
      `Clear your ${this.getModalTabLabel(draft)} details before submitting ${this.getModalTabLabel(methodId)}.`,
    );
    return false;
  }

  closeUploadSuccessModal(): void {
    this.isUploadSuccessModalOpen.set(false);
  }

  hasPreviewData(fileItem: NgsildUploadedFileItem): boolean {
    if (fileItem.status !== 'completed') {
      return false;
    }
    return (
      fileItem.previewData != null ||
      isDirectlyPreviewableFileName(fileItem.file.name)
    );
  }

  openFilePreview(fileItem: NgsildUploadedFileItem): void {
    const file = fileItem.file;
    const ext = this.getFileExtension(file.name);

    if (ext === '.csv' || ext === '.txt') {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = (event.target?.result as string) ?? '';
        const parsedData =
          ext === '.csv' ? parseNgsildPreviewData(content) : null;
        this.previewingFile.set({
          name: file.name,
          size: file.size,
          type: file.type,
          previewData: fileItem.previewData,
          parsedData:
            parsedData && parsedData.headers.length > 0 ? parsedData : null,
          rawText: ext === '.txt' ? content : null,
          previewUnavailableReason: null,
        });
        this.previewModalOpen.set(true);
      };
      reader.onerror = () => {
        this.showBanner('error', 'Failed to read file for preview');
      };
      reader.readAsText(file);
      return;
    }

    if (!fileItem.previewData) {
      this.showBanner('error', 'Preview not available for this file');
      return;
    }

    const parsedData = parseNgsildPreviewData(fileItem.previewData);
    const unsupported = isPreviewUnsupportedResponse(fileItem.previewData);
    this.previewingFile.set({
      name: file.name,
      size: file.size,
      type: file.type,
      previewData: fileItem.previewData,
      parsedData,
      rawText: null,
      previewUnavailableReason: unsupported
        ? getPreviewUnsupportedMessage(fileItem.previewData) ||
          'Preview not available for this file'
        : null,
    });
    this.previewModalOpen.set(true);
  }

  closePreview(): void {
    this.previewModalOpen.set(false);
    this.previewingFile.set(null);
  }

  toggleHttpMethodDropdown(): void {
    this.isHttpMethodDropdownOpen.update((v) => !v);
    this.isAuthenticationTypeDropdownOpen.set(false);
    this.isResponseFormatDropdownOpen.set(false);
  }

  selectHttpMethod(method: string): void {
    this.apiIntegrationForm.patchValue({ httpMethod: method });
    this.isHttpMethodDropdownOpen.set(false);
    this.apiIntegrationForm.get('httpMethod')?.markAsTouched();
  }

  getSelectedHttpMethod(): string {
    return (
      this.apiIntegrationForm.get('httpMethod')?.value || 'Select HTTP Method'
    );
  }

  isHttpMethodSelected(method: string): boolean {
    return this.apiIntegrationForm.get('httpMethod')?.value === method;
  }

  toggleAuthenticationTypeDropdown(): void {
    this.isAuthenticationTypeDropdownOpen.update((v) => !v);
    this.isHttpMethodDropdownOpen.set(false);
    this.isResponseFormatDropdownOpen.set(false);
  }

  selectAuthenticationType(type: string): void {
    this.apiIntegrationForm.patchValue({ authenticationType: type });
    this.isAuthenticationTypeDropdownOpen.set(false);
    this.apiIntegrationForm.get('authenticationType')?.markAsTouched();
  }

  getSelectedAuthenticationType(): string {
    return (
      this.apiIntegrationForm.get('authenticationType')?.value ||
      'Select Authentication Type'
    );
  }

  isAuthenticationTypeSelected(type: string): boolean {
    return this.apiIntegrationForm.get('authenticationType')?.value === type;
  }

  toggleResponseFormatDropdown(): void {
    this.isResponseFormatDropdownOpen.update((v) => !v);
    this.isHttpMethodDropdownOpen.set(false);
    this.isAuthenticationTypeDropdownOpen.set(false);
  }

  selectResponseFormat(format: string): void {
    this.apiIntegrationForm.patchValue({ responseFormat: format });
    this.isResponseFormatDropdownOpen.set(false);
    this.apiIntegrationForm.get('responseFormat')?.markAsTouched();
  }

  getSelectedResponseFormat(): string {
    return (
      this.apiIntegrationForm.get('responseFormat')?.value ||
      'Select Response Format'
    );
  }

  isResponseFormatSelected(format: string): boolean {
    return this.apiIntegrationForm.get('responseFormat')?.value === format;
  }

  @HostListener('document:keydown.escape')
  onEscapeClosePreview(): void {
    if (this.previewModalOpen()) {
      this.closePreview();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target?.closest?.('.custom-dropdown-wrapper')) return;
    this.isHttpMethodDropdownOpen.set(false);
    this.isAuthenticationTypeDropdownOpen.set(false);
    this.isResponseFormatDropdownOpen.set(false);
  }

  private datasetOnboardingPayload(): NgsildOnboardingPayload | null {
    if (this.assetKind() === 'model') {
      return null;
    }
    return (this.onboardingPayload() as NgsildOnboardingPayload | null) ?? null;
  }
}
